import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios';
import { rewriteElectronLocalRequestUrl } from '../config/runtime';
import { useAuthStore } from '../stores/auth';

/**
 * Create and configure axios instance with interceptors
 */
export function createApiClient(): AxiosInstance {
  const client = axios;

  client.interceptors.request.use((request) => {
    if (request.url) {
      request.url = rewriteElectronLocalRequestUrl(request.url);
    }
    return request;
  });

  // Response interceptor for handling 401 and token refresh
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Skip auth refresh for certain endpoints
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        const authStore = useAuthStore();

        // Only refresh when a local hub token exists — cloud tokens have their
        // own OAuth refresh flow and panel tokens their own refresh endpoint.
        if (!authStore.accessToken) {
          return Promise.reject(error);
        }

        try {
          // Attempt to refresh the local hub token (sliding session with
          // saved-credentials fallback)
          const refreshed = await authStore.refreshLocalHubToken();

          if (refreshed) {
            // Retry original request with new token
            const newToken = authStore.accessToken;
            if (newToken) {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            return client(originalRequest);
          } else {
            // Refresh failed — notify once and redirect to connect/login
            await authStore.notifySessionExpired();
            return Promise.reject(error);
          }
        } catch (refreshError) {
          console.error('[AxiosInterceptor] Token refresh failed:', refreshError);
          await authStore.notifySessionExpired();
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    },
  );

  return client;
}

// Create default instance
const apiClient = createApiClient();

export default apiClient;
